import { useAuth } from '@/hooks/use-auth';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { io, Socket } from 'socket.io-client';

export interface PresenceUser {
  userId: number;
  name: string;
  initials: string;
  color: string;
}

interface PipelinePresenceState {
  cards: Record<number, PresenceUser[]>;
  stages: Record<number, PresenceUser[]>;
  users: PresenceUser[];
  updatedAt?: string;
}

const INITIAL_STATE: PipelinePresenceState = {
  cards: {},
  stages: {},
  users: [],
};

function getInitials(name: string) {
  return name
    .split(' ')
    .filter(Boolean)
    .map((part) => part[0])
    .join('')
    .slice(0, 3)
    .toUpperCase();
}

function getColorFromId(id: number) {
  const hue = (id * 67) % 360;
  return `hsl(${hue}deg 78% 63%)`;
}

export function usePipelinePresence() {
  const { user } = useAuth();
  const socketRef = useRef<Socket | null>(null);
  const [presence, setPresence] = useState<PipelinePresenceState>(INITIAL_STATE);
  const latestSelectionRef = useRef<{ cardId: number | null; stageId: number | null }>({
    cardId: null,
    stageId: null,
  });
  const lastEmittedKeyRef = useRef<string>('none-none');

  const profile = useMemo(() => {
    if (!user?.id) return null;

    const name =
      [user.firstName, user.lastName].filter(Boolean).join(' ').trim() || user.name || 'Teammate';

    return {
      name,
      initials: getInitials(name),
      color: getColorFromId(user.id),
    };
  }, [user]);

  useEffect(() => {
    if (!user?.id || !profile) return;

    const socket = io(window.location.origin, {
      transports: ['websocket', 'polling'],
    });

    socketRef.current = socket;

    socket.on('connect', () => {
      socket.emit('authenticate', { userId: user.id });
    });

    socket.on('authenticated', () => {
      socket.emit('pipeline:presence:join', {
        name: profile.name,
        initials: profile.initials,
        color: profile.color,
        cardId: latestSelectionRef.current.cardId,
        stageId: latestSelectionRef.current.stageId,
      });
    });

    socket.on('pipeline:presence:update', (payload: PipelinePresenceState) => {
      setPresence({
        cards: payload?.cards ?? {},
        stages: payload?.stages ?? {},
        users: payload?.users ?? [],
        updatedAt: payload?.updatedAt,
      });
    });

    socket.on('disconnect', () => {
      setPresence(INITIAL_STATE);
    });

    return () => {
      socket.emit('pipeline:presence:leave');
      socket.disconnect();
      socketRef.current = null;
    };
  }, [user?.id, profile]);

  const updateCardPresence = useCallback((cardId: number | null, stageId: number | null) => {
    latestSelectionRef.current = { cardId, stageId };

    const key = `${cardId ?? 'none'}-${stageId ?? 'none'}`;
    if (key === lastEmittedKeyRef.current) {
      return;
    }
    lastEmittedKeyRef.current = key;

    if (socketRef.current) {
      socketRef.current.emit('pipeline:presence:update', {
        cardId,
        stageId,
      });
    }
  }, []);

  return {
    presence,
    updateCardPresence,
    currentUserId: user?.id ?? null,
    profile,
  };
}
